/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Platform,
  StyleSheet,
  View,
  type LayoutChangeEvent,
  type ViewStyle,
} from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';

import {
  TabBarView,
  TabViewStyle,
  TabsViewProps,
} from '@airasia-phoenix/react-native/components';
import ChipView from '@airasia-phoenix/react-native/components/ChipView';
import type { Post, SocialFeedPageV4Ref } from '@airasia-rn/social';
import { useSocialFeed, MovementsGridSkeletonView } from '@airasia-rn/social';
import { useLogger } from '@airasia-rn/core-logger';
import { ScrollView } from 'react-native-gesture-handler';

import ForYouFeed, { type ForYouFeedRef } from './ForYouFeed';
import PromotionFeed, { type PromotionFeedRef } from './PromotionsFeed';
import { SocialFeedWidget, SocialFeedWidgetV4 } from '../../widgets';
import {
  RecommendationEvents,
  trackRecommendationTabChange,
  trackMovementsViewBatch,
  getRecommendationTabEnglishTitleFromId,
  getRecommendationTabDisplayTitle,
  trackMovementsCategoryClick,
} from '../../analytics/recommendation';
import { pd } from '@airasia-phoenix/react-native/utils/utils';
import { useAppConfig } from '@airasia-rn/ui';
import { useMovementsV4LayoutEnabled } from '../../pages/HomePage/useHomePageRuntime';
import type { RecommendationCallbacks } from '../../types/home';
import type {
  MovementsItem,
  MovementsSection,
  MovementsResultData,
} from '../../models/recommendTabResponse';

import { styles } from './Recommend.styles';

const localStyles = StyleSheet.create({
  activeTabPane: {
    display: 'flex',
  },
  inactiveTabPane: {
    display: 'none',
  },
  categoryChipsScrollView: {
    paddingTop: 4,
    paddingBottom: 8,
  },
});

const normalizeTabId = (tabId: string) => String(tabId).toLowerCase();

const getSafeTabIndex = (index: number | undefined, tabsLength: number) => {
  if (index === undefined || index < 0 || index >= tabsLength) {
    return 0;
  }

  return index;
};

export const getDefaultRecommendationTabIndex = (
  tabs: {
    id: string;
    title: string;
  }[],
  {
    hasUpcomingBooking,
    isMovementsEnabled,
  }: {
    hasUpcomingBooking?: boolean;
    isMovementsEnabled?: boolean;
  } = {},
) => {
  if (!tabs.length) {
    return 0;
  }

  const forYouIndex = tabs.findIndex(
    (tab) => normalizeTabId(tab.id) === 'for_you',
  );
  const movementsIndex = tabs.findIndex(
    (tab) => normalizeTabId(tab.id) === 'movements',
  );
  const shouldFocusMovements =
    hasUpcomingBooking === false &&
    (isMovementsEnabled ?? movementsIndex >= 0) &&
    movementsIndex >= 0;

  if (hasUpcomingBooking || !shouldFocusMovements) {
    return forYouIndex >= 0 ? forYouIndex : 0;
  }

  return movementsIndex >= 0
    ? movementsIndex
    : forYouIndex >= 0
      ? forYouIndex
      : 0;
};

export interface RecommendedTabViewRef {
  maybeLoadMoreOnScroll: () => void;
}

interface RecommendedTabViewProps {
  tabs: {
    id: string;
    title: string;
  }[];
  renderMode?: 'full' | 'tabBar' | 'content';
  defaultTabIndex?: number;
  controlledTabIndex?: number;
  onTabIndexChange?: (index: number) => void;
  openModule?: (data: { widget: string; deeplink?: string }) => void;
  recommendationCallbacks?: RecommendationCallbacks;
  onForYouShowMorePress?: (params: {
    tabName: string;
    quartile: number;
    maxIndex: number;
  }) => void;
}

const RecommendedTabView = forwardRef<
  RecommendedTabViewRef,
  RecommendedTabViewProps
>(
  (
    {
      tabs,
      renderMode = 'full',
      defaultTabIndex,
      controlledTabIndex,
      onTabIndexChange,
      openModule,
      recommendationCallbacks,
      onForYouShowMorePress,
    },
    ref,
  ) => {
    const filteredTabs = useMemo(() => {
      if (Platform.OS !== 'web') return tabs;
      return tabs.filter((tab) => normalizeTabId(tab.id) !== 'movements');
    }, [tabs]);

    const normalizedDefaultTabIndex = useMemo(
      () => getSafeTabIndex(defaultTabIndex, filteredTabs.length),
      [defaultTabIndex, filteredTabs.length],
    );
    const [internalTabIndex, setInternalTabIndex] = useState(
      normalizedDefaultTabIndex,
    );
    const hasUserSelectedTabRef = useRef(false);
    const forYouRef = useRef<ForYouFeedRef>(null);
    const movementsV4Ref = useRef<SocialFeedPageV4Ref>(null);
    const promotionRef = useRef<PromotionFeedRef>(null);
    const [measuredContentHeights, setMeasuredContentHeights] = useState<
      Record<number, number>
    >({});
    const [transientContentHeight, setTransientContentHeight] = useState(0);
    const tabIdsSignature = useMemo(
      () => filteredTabs.map((tab) => normalizeTabId(tab.id)).join('|'),
      [filteredTabs],
    );
    const activeIndex =
      controlledTabIndex === undefined ? internalTabIndex : controlledTabIndex;
    const safeActiveIndex =
      activeIndex >= 0 && activeIndex < filteredTabs.length ? activeIndex : 0;
    // Mount only the active pane first so inactive tabs do not do work on the
    // initial homepage render.
    const [mountedTabIndexes, setMountedTabIndexes] = useState<number[]>(() =>
      filteredTabs.length ? [safeActiveIndex] : [],
    );
    const previousTabIdsSignatureRef = useRef(tabIdsSignature);

    useEffect(() => {
      if (previousTabIdsSignatureRef.current === tabIdsSignature) {
        return;
      }

      previousTabIdsSignatureRef.current = tabIdsSignature;
      hasUserSelectedTabRef.current = false;
      setMountedTabIndexes(filteredTabs.length ? [safeActiveIndex] : []);
      setMeasuredContentHeights({});
      setTransientContentHeight(0);
    }, [filteredTabs.length, safeActiveIndex, tabIdsSignature]);

    useEffect(() => {
      if (controlledTabIndex !== undefined || hasUserSelectedTabRef.current) {
        return;
      }

      if (defaultTabIndex === undefined) {
        return;
      }

      setInternalTabIndex(normalizedDefaultTabIndex);
    }, [
      controlledTabIndex,
      normalizedDefaultTabIndex,
      defaultTabIndex,
      tabIdsSignature,
    ]);

    useEffect(() => {
      setMountedTabIndexes((previousIndexes) => {
        if (!filteredTabs.length || previousIndexes.includes(safeActiveIndex)) {
          return previousIndexes;
        }

        // Keep already-visited panes mounted so tab switches stay instant.
        return [...previousIndexes, safeActiveIndex];
      });
    }, [filteredTabs.length, safeActiveIndex]);

    useImperativeHandle(
      ref,
      () => ({
        maybeLoadMoreOnScroll: () => {
          const activeTabId = normalizeTabId(
            filteredTabs[safeActiveIndex]?.id ?? '',
          );
          if (activeTabId === 'for_you') {
            forYouRef.current?.maybeLoadMoreOnScroll();
          } else if (activeTabId === 'promotions') {
            promotionRef.current?.maybeLoadMoreOnScroll();
          } else if (activeTabId === 'movements') {
            movementsV4Ref.current?.maybeLoadMoreOnScroll();
          }
        },
      }),
      [filteredTabs, safeActiveIndex, renderMode],
    );

    const tabData = useMemo(
      () =>
        filteredTabs.map((tab, index) => ({
          key: String(index),
          text: getRecommendationTabDisplayTitle(tab),
        })),
      [filteredTabs],
    );

    const isV4LayoutEnabled = useMovementsV4LayoutEnabled();

    // Only fire the v4 request when the v4 layout is actually active. The
    // legacy branch renders SocialFeedWidget, which fires its own v1
    // request via useSocialFeedLegacy() — without this gate both v1 and v4
    // requests would fire together whenever the flag is off.
    const movementsHomeQuery = useSocialFeed({ enabled: isV4LayoutEnabled });
    const movementsCategories = useMemo(() => {
      return movementsHomeQuery.data?.pages?.[0]?.categories || [];
    }, [movementsHomeQuery.data]);
    const hasChips = movementsCategories.length > 0;

    const { logEvent } = useLogger();
    const logMovementsEvent = logEvent;
    const { config } = useAppConfig();
    const movementsViewedLastIndexRef = useRef(0);

    const handleMovementsResultState = useCallback(
      (data: MovementsResultData) => {
        // Handle V4 payload (flat data array)
        if (data?.data && Array.isArray(data.data)) {
          const items = data.data;
          const posts: Post[] = items.map(
            (item: MovementsItem, index: number) => ({
              post_id: item.itemId || item.lobItemId || `post_${index}`,
              rank: index + 1,
              media: {
                type: item.video ? 'VIDEO' : 'IMAGE',
                thumbnail: { url: item.image || '', width: 360, height: 640 },
                duration_ms: item.video ? 15000 : 0,
              },
              snippet: item.title || '',
              deeplink: item.deepLink || '',
            }),
          );

          while (posts.length - movementsViewedLastIndexRef.current >= 3) {
            const start = movementsViewedLastIndexRef.current;
            const batch = posts.slice(start, start + 3);
            logMovementsEvent(
              RecommendationEvents.View_Item_List,
              trackMovementsViewBatch(batch, start, config.currency ?? 'MYR'),
            );
            movementsViewedLastIndexRef.current += 3;
          }
          return;
        }

        // Handle Legacy payload (sections array)
        const posts: Post[] = (data?.sections ?? []).flatMap(
          (s: MovementsSection) => s.posts ?? [],
        );
        while (posts.length - movementsViewedLastIndexRef.current >= 3) {
          const start = movementsViewedLastIndexRef.current;
          const batch = posts.slice(start, start + 3);
          logMovementsEvent(
            RecommendationEvents.View_Item_List,
            trackMovementsViewBatch(batch, start, config.currency ?? 'MYR'),
          );
          movementsViewedLastIndexRef.current += 3;
        }
      },
      [logMovementsEvent, config.currency],
    );

    const handleMovementsFeedItemClick = useCallback(
      (post: Post) => {
        if (recommendationCallbacks?.onCardPress) {
          recommendationCallbacks.onCardPress({
            itemId: post.post_id,
            lob: 'socialProfile',
            title: post.snippet,
            deepLink: post.deeplink,
          });
          return;
        }

        openModule?.({ widget: 'socialProfile', deeplink: post.deeplink });
      },
      [openModule, recommendationCallbacks],
    );

    const handleMovementsChipClick = useCallback(
      (
        deeplink: string | undefined,
        location: any,
        category: any,
        index: number | undefined,
      ) => {
        if (!deeplink) return;

        if (category && typeof index === 'number') {
          logEvent(
            RecommendationEvents.tap,
            trackMovementsCategoryClick(
              category.title,
              category.tag,
              index + 1,
            ),
          );
        }

        if (recommendationCallbacks?.onCardPress) {
          recommendationCallbacks.onCardPress({
            itemId: category?.key || `chip_${index}`,
            lob: 'socialProfile',
            title: category?.title || '',
            deepLink: deeplink,
          });
          return;
        }

        openModule?.({ widget: 'socialProfile', deeplink });
      },
      [logEvent, recommendationCallbacks, openModule],
    );

    const renderTabPane = useCallback(
      (tab: { id: string; title: string }) => {
        const tabId = String(tab.id).toLowerCase();
        switch (tabId) {
          case 'for_you':
            return (
              <ForYouFeed
                ref={forYouRef}
                tabName={tab.title.toLowerCase()}
                onRecommendationPress={recommendationCallbacks?.onCardPress}
                onPressCta={recommendationCallbacks?.onPressCta}
                onShowMorePress={onForYouShowMorePress}
              />
            );
          case 'promotions':
            return (
              <PromotionFeed
                ref={promotionRef}
                tabName={tab.title.toLowerCase()}
                onRecommendationPress={recommendationCallbacks?.onCardPress}
                onPressCta={recommendationCallbacks?.onPressCta}
              />
            );
          case 'movements': {
            return isV4LayoutEnabled ? (
              // The sticky tab bar owns the category chips row, so the feed
              // only needs a small gap below it. The page's own chips row is
              // disabled to avoid a hidden duplicate touchable row.
              <View>
                {!movementsHomeQuery.data && movementsHomeQuery.isPending ? (
                  <MovementsGridSkeletonView />
                ) : (
                  <SocialFeedWidgetV4
                    ref={movementsV4Ref}
                    recommendationHashKey={
                      process.env.EXPO_PUBLIC_RECOMMENDATION_HASH_KEY ?? ''
                    }
                    // Show animation whenever data is available (cached or fresh fetch).
                    // Shimmer only appears if no data yet (first load, or API in-flight).
                    runFeedAnimation={true}
                    maxPostsPerSection={3}
                    maxSections={3}
                    maxChips={3}
                    openModule={openModule}
                    onSectionHeaderClick={(deeplink: string) =>
                      openModule?.({ widget: 'socialProfile', deeplink })
                    }
                    onFeedItemClick={handleMovementsFeedItemClick}
                    onFooterChipClick={handleMovementsChipClick}
                    onResultState={handleMovementsResultState}
                    showCategoryChips={false}
                  />
                )}
              </View>
            ) : (
              <SocialFeedWidget
                recommendationHashKey={
                  process.env.EXPO_PUBLIC_RECOMMENDATION_HASH_KEY ?? ''
                }
                runFeedAnimation={true}
                maxPostsPerSection={3}
                maxSections={3}
                maxChips={3}
                openModule={openModule}
                onSectionHeaderClick={(deeplink: string) =>
                  openModule?.({ widget: 'socialProfile', deeplink })
                }
                onFeedItemClick={handleMovementsFeedItemClick}
                onFooterChipClick={handleMovementsChipClick}
                onResultState={handleMovementsResultState}
              />
            );
          }
          default:
            return null;
        }
      },
      [
        handleMovementsFeedItemClick,
        handleMovementsChipClick,
        handleMovementsResultState,
        openModule,
        recommendationCallbacks?.onCardPress,
        recommendationCallbacks?.onPressCta,
        onForYouShowMorePress,
        // The movements pane's skeleton guard reads these — without them the
        // callback keeps the first render's closure (isPending: true) forever,
        // so on a cold first load the pane stays on the skeleton even after
        // the feed query succeeds. (renderTabContent memoizes on this
        // callback's identity, so a stale identity freezes the whole pane.)
        movementsHomeQuery.data,
        movementsHomeQuery.isPending,
        hasChips,
        isV4LayoutEnabled,
      ],
    );
    // Native FlatList keeps scroll offset, so preserve pane height across tabs.
    const shouldPreserveContentHeight =
      Platform.OS !== 'web' && renderMode === 'content';

    const handleActivePaneLayout = useCallback(
      (event: LayoutChangeEvent) => {
        if (!shouldPreserveContentHeight) {
          return;
        }

        const height = event.nativeEvent.layout.height;
        if (height <= 0) {
          return;
        }

        setTransientContentHeight(height);
        setMeasuredContentHeights((currentHeights) =>
          currentHeights[safeActiveIndex] === height
            ? currentHeights
            : { ...currentHeights, [safeActiveIndex]: height },
        );
      },
      [safeActiveIndex, shouldPreserveContentHeight],
    );

    const activeTabId = normalizeTabId(filteredTabs[safeActiveIndex]?.id ?? '');

    const activeContentHeight =
      measuredContentHeights[safeActiveIndex] ?? transientContentHeight;

    const preservedContentHeight =
      shouldPreserveContentHeight &&
      activeContentHeight > 0 &&
      activeTabId !== 'movements'
        ? activeContentHeight
        : 0;
    const contentHeightStyle = useMemo<ViewStyle | undefined>(
      () =>
        preservedContentHeight > 0
          ? { minHeight: preservedContentHeight }
          : undefined,
      [preservedContentHeight],
    );
    const contentTopSpacingStyle = useMemo(() => {
      if (renderMode !== 'content' && renderMode !== 'full') {
        return undefined;
      }

      return activeTabId === 'movements'
        ? {
            paddingTop: 0,
          }
        : {
            paddingTop: pd.ref.space.spacing20.px,
          };
    }, [activeTabId, renderMode]);

    const onSelectTab = useCallback(
      (item: TabsViewProps) => {
        const nextIndex = Number(item.key);
        const selectedTab = filteredTabs?.[nextIndex];
        const selectedTabTitle = selectedTab
          ? getRecommendationTabEnglishTitleFromId(selectedTab.id)
          : undefined;
        if (selectedTabTitle) {
          logEvent(
            RecommendationEvents.tap,
            trackRecommendationTabChange(selectedTabTitle, selectedTab?.id),
          );
        }
        hasUserSelectedTabRef.current = true;
        if (onTabIndexChange) {
          onTabIndexChange(nextIndex);
          return;
        }
        setInternalTabIndex(nextIndex);
      },
      [filteredTabs, logEvent, onTabIndexChange],
    );

    const swipeGesture = useMemo(
      () =>
        Gesture.Pan()
          .activeOffsetX([-20, 20])
          .failOffsetY([-10, 10])
          .runOnJS(true)
          .onEnd((event) => {
            const SWIPE_THRESHOLD = 50;
            if (event.translationX < -SWIPE_THRESHOLD) {
              const nextIndex = safeActiveIndex + 1;
              if (nextIndex < filteredTabs.length) {
                onSelectTab({ key: String(nextIndex) } as TabsViewProps);
              }
            } else if (event.translationX > SWIPE_THRESHOLD) {
              const prevIndex = safeActiveIndex - 1;
              if (prevIndex >= 0) {
                onSelectTab({ key: String(prevIndex) } as TabsViewProps);
              }
            }
          }),
      [safeActiveIndex, filteredTabs.length, onSelectTab],
    );

    const renderTabContent = useMemo(() => {
      if (!filteredTabs.length) return null;

      const content = (
        <View style={[contentHeightStyle, contentTopSpacingStyle]}>
          {mountedTabIndexes.map((index) => {
            const tab = filteredTabs[index];
            if (!tab) return null;
            const isActive = index === safeActiveIndex;

            return (
              <View
                key={tabData[index]?.key ?? String(index)}
                onLayout={
                  shouldPreserveContentHeight && isActive
                    ? handleActivePaneLayout
                    : undefined
                }
                style={
                  isActive
                    ? localStyles.activeTabPane
                    : localStyles.inactiveTabPane
                }
              >
                {renderTabPane(tab)}
              </View>
            );
          })}
        </View>
      );

      if (Platform.OS === 'web') {
        return content;
      }

      // On Android, use swipe gestures carefully to avoid interfering with tab clicks
      if (Platform.OS === 'android') {
        return content;
      }

      return (
        <GestureDetector gesture={swipeGesture}>{content}</GestureDetector>
      );
    }, [
      filteredTabs,
      contentHeightStyle,
      contentTopSpacingStyle,
      handleActivePaneLayout,
      mountedTabIndexes,
      renderTabPane,
      safeActiveIndex,
      shouldPreserveContentHeight,
      swipeGesture,
      tabData,
    ]);

    //EMPTY

    if (!filteredTabs?.length) return null;

    const renderTabBar = () => {
      const isMovementsActive =
        normalizeTabId(filteredTabs[safeActiveIndex]?.id ?? '') === 'movements';
      return (
        <View
          style={{
            backgroundColor: pd.sys.colour.bg_primary.toString(),
            ...(Platform.OS === 'android'
              ? { zIndex: 0, elevation: 4, shadowColor: 'transparent' }
              : { zIndex: 10 }),
          }}
        >
          <TabBarView
            data={tabData}
            peek={0}
            tabStyle={TabViewStyle.STANDARD}
            contentAlignment={(tabData?.length ?? 0) >= 3 ? 'center' : 'start'}
            currentTabIndex={safeActiveIndex}
            containerWidth="100%"
            onSelect={onSelectTab}
            isScrollable={false}
          />
          {isMovementsActive && hasChips && isV4LayoutEnabled && (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={localStyles.categoryChipsScrollView}
              contentContainerStyle={{ paddingHorizontal: 16 }}
              keyboardShouldPersistTaps="handled"
            >
              {movementsCategories.map((cat: any, index: number) => {
                const isSelected = !cat.deepLink;
                return (
                  <View
                    key={cat.key || String(index)}
                    style={{ marginRight: 8 }}
                  >
                    <ChipView
                      disabled={false}
                      isSelected={isSelected}
                      text={cat.title}
                      onPress={() =>
                        handleMovementsChipClick(cat.deepLink, null, cat, index)
                      }
                    />
                  </View>
                );
              })}
            </ScrollView>
          )}
        </View>
      );
    };

    if (renderMode === 'tabBar') {
      return renderTabBar();
    }

    if (renderMode === 'content') {
      return renderTabContent;
    }

    //MAIN RENDER

    return (
      <View style={styles.container}>
        {renderTabBar()}
        {renderTabContent}
      </View>
    );
  },
);

RecommendedTabView.displayName = 'RecommendedTabView';
export default RecommendedTabView;
